import {
  input as inputEl,
  textarea as textareaEl,
  type AttrValue,
  type ElProps,
  type Mountable,
  type Reactive,
} from '@llui/dom';

/**
 * `<Input>` — the canonical single-line text control (per `30-design-system.md §8`).
 *
 * The shared home for the ~74 bare `input(...)` call sites. Token-correct
 * chrome (surface-base, border-default, radius-md, focus ring on
 * border-strong) matched to `selectInput` so inputs and selects sit on
 * the same visual baseline. Native semantics preserved — keyboard,
 * screen-reader, and mobile keyboards are free.
 *
 * UNREVIEWED (Phase A): minimal baseline; flagged in `/ui-gallery`.
 *
 * Reactive-friendly: `value`, `invalid`, and `disabled` accept a static
 * value or a `Signal<…>`. The `invalid` flag toggles the
 * `dl-input--invalid` class AND sets `aria-invalid` (color is never the
 * only signal). Arbitrary attributes (id, name, autocomplete, `aria-*`,
 * `data-*`, reactive bindings) ride through `attrs`.
 *
 * The single-line `textInput` and the multi-line `textArea` share the
 * same chrome and option shape so the rewire of the 6 textarea sites is
 * the same mechanical swap.
 *
 * Two `variant`s: `field` (default) is the canonical bordered input;
 * `bare` is borderless for inline surfaces (the command palette) where
 * the surrounding row owns the framing + focus ring.
 */

/** Visual chrome: a bordered field (default) or a borderless inline input. */
export type TextInputVariant = 'field' | 'bare';

export interface TextInputOptions {
  /** Visual chrome — `field` (bordered, default) or `bare` (borderless inline). */
  variant?: TextInputVariant;
  /** Controlled value — static or reactive. */
  value?: Reactive<string>;
  /** Input handler — fires on every keystroke with the new value. */
  onInput?: (value: string, ev: Event) => void;
  /** Change handler — fires on commit (blur / Enter). */
  onChange?: (value: string, ev: Event) => void;
  /** Native input type (single-line only). Defaults to `'text'`. */
  type?: string;
  /** Placeholder text. */
  placeholder?: AttrValue;
  /** Invalid state — toggles the invalid class + `aria-invalid`. */
  invalid?: Reactive<boolean>;
  /** Disabled state — static or reactive. */
  disabled?: Reactive<boolean>;
  /** Forwarded `data-testid`. */
  testid?: string;
  /** Accessible label (when no associated `<label for>`). */
  ariaLabel?: AttrValue;
  /** Extra class(es) appended after the base + state classes. */
  className?: string;
  /** Arbitrary extra attributes merged onto the root (spread first). */
  attrs?: ElProps;
}

export interface TextAreaOptions extends TextInputOptions {
  /** Visible row count. */
  rows?: AttrValue;
}

/** Base + invalid + caller class. Reactive when `invalid` is a signal.
 * The invalid modifier always attaches to `dl-input` (the first base
 * token), so `textArea`'s `dl-input dl-textarea` base still gets
 * `dl-input--invalid`. */
function inputClass(
  base: string,
  invalid: Reactive<boolean> | undefined,
  className: string | undefined,
): AttrValue {
  const extra = className ? ` ${className}` : '';
  const invalidMod = 'dl-input--invalid';
  if (invalid === undefined || invalid === false) return `${base}${extra}`;
  if (invalid === true) return `${base} ${invalidMod}${extra}`;
  // Signal<boolean> — derive reactively.
  return invalid.map((v) => (v ? `${base} ${invalidMod}${extra}` : `${base}${extra}`));
}

/** `aria-invalid` mirror of `invalid` — reactive when `invalid` is a signal. */
function ariaInvalid(invalid: Reactive<boolean> | undefined): AttrValue | undefined {
  if (invalid === undefined || invalid === false) return undefined;
  if (invalid === true) return 'true';
  return invalid.map((v) => (v ? 'true' : 'false'));
}

function commonProps(opts: TextInputOptions, base: string): ElProps {
  return {
    ...(opts.attrs ?? {}),
    class: inputClass(base, opts.invalid, opts.className),
    ...(opts.value !== undefined ? { value: opts.value } : {}),
    ...(opts.placeholder !== undefined ? { placeholder: opts.placeholder } : {}),
    ...(opts.disabled !== undefined ? { disabled: opts.disabled } : {}),
    ...(opts.testid !== undefined ? { 'data-testid': opts.testid } : {}),
    ...(opts.ariaLabel !== undefined ? { 'aria-label': opts.ariaLabel } : {}),
    ...(ariaInvalid(opts.invalid) !== undefined ? { 'aria-invalid': ariaInvalid(opts.invalid) } : {}),
    ...(opts.onInput
      ? { onInput: (ev: Event) => opts.onInput!((ev.target as HTMLInputElement).value, ev) }
      : {}),
    ...(opts.onChange
      ? { onChange: (ev: Event) => opts.onChange!((ev.target as HTMLInputElement).value, ev) }
      : {}),
  };
}

/** Base token list for a variant — `bare` appends the borderless modifier. */
function baseClass(lead: string, variant: TextInputVariant | undefined): string {
  return variant === 'bare' ? `${lead} dl-input--bare` : lead;
}

export function textInput(opts: TextInputOptions): Mountable {
  const props = commonProps(opts, baseClass('dl-input', opts.variant));
  props.type = opts.type ?? 'text';
  return inputEl(props);
}

export function textArea(opts: TextAreaOptions): Mountable {
  const props = commonProps(opts, baseClass('dl-input dl-textarea', opts.variant));
  if (opts.rows !== undefined) props.rows = opts.rows;
  return textareaEl(props);
}
